var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, LayoutContent, LayoutContentBody, LayoutContentHeader, LayoutControls, Typography, useTheme, } from '@pinwheel/origami';
import { useAppDispatch, useAppSelector } from '../../state';
import { hideBottomSheet, selectBottomSheet, selectBottomSheetEventId, selectBottomSheetShown, } from '../../state/slices/slice-bottom-sheet';
import BigStatusIcon from '../../assets/big-status/BigStatusIcon';
import useAnimationSlideUp, { BOTTOM_SHEET_ANIMATE_S, } from './useAnimationSlideUp';
import eventQueue from '../../events/queue';
import useLinkNavigate from '../../hooks/useLinkNavigate';
import { setSelectedPlatform as dispatchSetSelectedPlatform } from '../../state/slices/slice-provider-entity';
import { selectMatchedPlatforms, setUsingMultiSupplierEligibility, } from '../../state/slices/slice-link-api';
import { selectFirstEligiblePlatform } from '../../state/slices/slice-supplier-eligibility';
export function BottomSheet() {
    var resolveColor = useTheme().resolveColor;
    var dispatch = useAppDispatch();
    var navigate = useLinkNavigate();
    var firstEligiblePlatform = useAppSelector(selectFirstEligiblePlatform);
    var matchedPlatforms = useAppSelector(selectMatchedPlatforms);
    var eventId = useAppSelector(selectBottomSheetEventId);
    var _a = useAppSelector(selectBottomSheet), title = _a.title, body = _a.body, dismissButtonText = _a.dismissButtonText, dismissButtonVariant = _a.dismissButtonVariant, navigateButtonText = _a.navigateButtonText, navigateButtonVariant = _a.navigateButtonVariant, navigateButtonAction = _a.navigateButtonAction, imageId = _a.imageId;
    var navigateButtonCallback = useCallback(function () {
        if (navigateButtonAction === 'loginWithMatchedPlatform' ||
            navigateButtonAction === 'loginWithADPTestPlatform') {
            if (firstEligiblePlatform)
                dispatch(dispatchSetSelectedPlatform(firstEligiblePlatform));
            else
                dispatch(dispatchSetSelectedPlatform(matchedPlatforms[0].platform));
            dispatch(setUsingMultiSupplierEligibility(true));
            eventQueue.emit('bottom_sheet', {
                isOpen: false,
                eventId: navigateButtonAction,
            });
            dispatch(hideBottomSheet());
            navigate('/link-api/login');
        }
    }, [
        dispatch,
        firstEligiblePlatform,
        navigate,
        navigateButtonAction,
        matchedPlatforms,
    ]);
    var slideDownCb = useCallback(function () {
        eventQueue.emit('bottom_sheet', { isOpen: false, eventId: eventId });
        dispatch(hideBottomSheet());
    }, [dispatch, eventId]);
    // When we first render this component, `refSheet` may not be initialized due to
    // race conditions and/or unknown reasons. The stateRef variable ensures that
    // we pick up the change when the `refSheet` value updates.
    var refSheet = useRef(null);
    var _b = useState(null), stateRef = _b[0], setStateRef = _b[1];
    useEffect(function () { return setStateRef(refSheet); }, [refSheet]);
    var _c = useAnimationSlideUp(stateRef, slideDownCb), style = _c.style, slideDown = _c.slideDown, refBg = _c.refBg;
    var dismiss = useCallback(slideDown, [slideDown]);
    return (React.createElement("div", { "data-testid": "BottomSheet-Backdrop", style: {
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'rgb(0,0,0,0)',
            // Need z index to go above the close button
            zIndex: 999,
            transition: "background ".concat(BOTTOM_SHEET_ANIMATE_S, "s"),
        }, onClick: dismiss, ref: refBg },
        React.createElement("div", { "data-testid": "BottomSheet", style: __assign({ position: 'absolute', background: resolveColor({
                    type: 'theme',
                    colorLegacy: 'Neutral/0',
                    colorNext: 'background/default',
                }), width: '100%', display: 'flex', flexDirection: 'column', borderRadius: '30px 30px 0 0' }, style), onClick: function (e) { return e.stopPropagation(); }, ref: refSheet },
            React.createElement(Box, { "data-testid": "BottomSheetTop", sx: { height: '64px' } },
                React.createElement(Box, { "data-testid": "BottomSheet-BarClickable", sx: {
                        margin: '0 auto',
                        padding: '16px',
                        width: '182px',
                        cursor: 'pointer',
                    }, onClick: dismiss },
                    React.createElement(Box, { sx: {
                            backgroundColor: {
                                type: 'theme',
                                colorLegacy: 'Neutral/20',
                                colorNext: 'neutral/default',
                                opacityNext: 0.5,
                            },
                            height: '5px',
                            width: '100%',
                            borderRadius: '2.5px',
                        } }))),
            React.createElement(LayoutContent, { sx: { marginBottom: '24px' } },
                React.createElement(LayoutContentHeader, { sx: { margin: 0 } },
                    React.createElement(BigStatusIcon, { iconName: imageId })),
                React.createElement(LayoutContentBody, { sx: { margin: '24px 0' } },
                    React.createElement(Typography, { as: "h1", sx: {
                            textAlign: 'left',
                            color: {
                                type: 'theme',
                                colorLegacy: 'Blue/70',
                                colorNext: 'brand/accent',
                            },
                            width: '100%',
                            marginBottom: '8px',
                            fontWeight: 600,
                        }, align: "left", variant: "header" }, title),
                    React.createElement(Typography, null, body))),
            React.createElement(LayoutControls, null,
                navigateButtonText && navigateButtonAction ? (React.createElement(Button, { onClick: navigateButtonCallback, variant: navigateButtonVariant, "data-testid": "BottomSheet-navigate" }, navigateButtonText)) : null,
                React.createElement(Button, { onClick: dismiss, variant: dismissButtonVariant }, dismissButtonText)))));
}
export default function BottomSheetContainer() {
    var isShown = useAppSelector(selectBottomSheetShown);
    if (!isShown)
        return null;
    return React.createElement(BottomSheet, null);
}
